import { observeResize } from '@affine/component';
import type {
  PageTextLine,
  PDF,
  PdfEditStore,
  PdfHighlightEdit,
  PdfInkEdit,
  PdfRect,
  PdfTextAddEdit,
  PdfTextReplaceEdit,
  PdfWhiteoutEdit,
} from '@affine/core/modules/pdf';
import {
  usePdfPageEdits,
  usePdfTextLayer,
  useYText,
} from '@affine/core/modules/pdf/edits/react';
import type { PageSize } from '@affine/core/modules/pdf/renderer/types';
import {
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import * as styles from './edit-styles.css';

export type PdfEditMode = 'view' | 'text' | 'draw' | 'highlight' | 'erase';

const DEFAULT_TEXT_STYLE = {
  fontSize: 14,
  fontWeight: 400,
  fontFamily: 'Helvetica',
  color: '#141414',
};
const HIGHLIGHT_COLOR = '#ffeb3b';
const INK_COLOR = '#e8433e';
const INK_WIDTH = 2;

function cssFontFamily(family: string): string {
  const lower = family.toLowerCase();
  if (lower.includes('times') || lower.includes('serif')) {
    return "'Times New Roman', Georgia, serif";
  }
  if (lower.includes('courier') || lower.includes('mono')) {
    return "'Courier New', monospace";
  }
  return 'Helvetica, Arial, sans-serif';
}

function rectContains(rect: PdfRect, x: number, y: number): boolean {
  return (
    x >= rect.x && x <= rect.x + rect.w && y >= rect.y && y <= rect.y + rect.h
  );
}

function rectsIntersect(a: PdfRect, b: PdfRect): boolean {
  return (
    a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y
  );
}

function lineToReplaceEdit(line: PageTextLine) {
  const rects = line.spans.map(span => ({
    x: span.rect[0],
    y: span.rect[1],
    w: span.rect[2],
    h: span.rect[3],
  }));
  // Style of the widest span wins; a line is usually one style anyway.
  const primary = line.spans.reduce((widest, span) =>
    span.rect[2] > widest.rect[2] ? span : widest
  );
  return {
    rects,
    box: {
      x: line.rect[0],
      y: line.rect[1],
      w: line.rect[2] + 8,
      h: line.rect[3],
    },
    initialText: line.spans.map(span => span.text).join(''),
    style: {
      fontSize: primary.fontSize,
      fontWeight: primary.fontWeight,
      fontFamily: primary.fontFamily,
      color: primary.color,
    },
  };
}

type OverlayProps = {
  pdf: PDF;
  store: PdfEditStore;
  pageNum: number;
  /** page size in PDF points */
  actualSize: PageSize;
  mode: PdfEditMode;
  author?: string;
};

export const PdfPageEditOverlay = ({
  pdf,
  store,
  pageNum,
  actualSize,
  mode,
  author,
}: OverlayProps) => {
  // The overlay tracks its own rendered size, so it stays aligned with the
  // page canvas even when CSS clamps the page below its requested size.
  const [cssWidth, setCssWidth] = useState(0);
  const scale =
    actualSize.width > 0 && cssWidth > 0 ? cssWidth / actualSize.width : 0;
  const edits = usePdfPageEdits(store, pageNum);
  const needsTextLayer = mode === 'text' || mode === 'highlight';
  const textLayer = usePdfTextLayer(pdf, pageNum, needsTextLayer);

  const [activeEditId, setActiveEditId] = useState<string | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [draftStroke, setDraftStroke] = useState<number[] | null>(null);
  const [draftRect, setDraftRect] = useState<PdfRect | null>(null);
  const dragStart = useRef<{ x: number; y: number } | null>(null);

  const toPagePoint = useCallback(
    (event: ReactPointerEvent) => {
      const overlay = overlayRef.current;
      if (!overlay) return null;
      const bounds = overlay.getBoundingClientRect();
      return {
        x: (event.clientX - bounds.left) / scale,
        y: (event.clientY - bounds.top) / scale,
      };
    },
    [scale]
  );

  const eraseEdit = useCallback(
    (id: string) => {
      if (mode !== 'erase') return;
      store.deleteEdit(id);
    },
    [mode, store]
  );

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const point = toPagePoint(event);
      if (!point) return;

      if (mode === 'text') {
        // Clicks on existing edit textareas are handled by the textareas.
        if (event.target !== event.currentTarget) return;

        const line = textLayer?.lines.find(candidate => {
          const [x, y, w, h] = candidate.rect;
          return rectContains({ x, y, w, h }, point.x, point.y);
        });

        if (line) {
          const replace = lineToReplaceEdit(line);
          const id = store.addTextReplace({
            page: pageNum,
            author,
            ...replace,
          });
          setActiveEditId(id);
        } else {
          const id = store.addTextAdd({
            page: pageNum,
            author,
            box: {
              x: point.x,
              y: point.y - DEFAULT_TEXT_STYLE.fontSize / 2,
              w: 160,
              h: DEFAULT_TEXT_STYLE.fontSize * 1.4,
            },
            initialText: '',
            style: DEFAULT_TEXT_STYLE,
          });
          setActiveEditId(id);
        }
        return;
      }

      if (mode === 'draw') {
        event.currentTarget.setPointerCapture(event.pointerId);
        setDraftStroke([point.x, point.y]);
        return;
      }

      if (mode === 'highlight') {
        event.currentTarget.setPointerCapture(event.pointerId);
        dragStart.current = point;
        setDraftRect({ x: point.x, y: point.y, w: 0, h: 0 });
      }
    },
    [mode, toPagePoint, textLayer, store, pageNum, author]
  );

  const handlePointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const point = toPagePoint(event);
      if (!point) return;

      if (mode === 'draw' && draftStroke) {
        const lastX = draftStroke[draftStroke.length - 2];
        const lastY = draftStroke[draftStroke.length - 1];
        const distance = Math.hypot(point.x - lastX, point.y - lastY);
        if (distance * scale < 1.5) return;
        setDraftStroke([...draftStroke, point.x, point.y]);
        return;
      }

      if (mode === 'highlight' && dragStart.current) {
        const start = dragStart.current;
        setDraftRect({
          x: Math.min(start.x, point.x),
          y: Math.min(start.y, point.y),
          w: Math.abs(point.x - start.x),
          h: Math.abs(point.y - start.y),
        });
      }
    },
    [mode, toPagePoint, draftStroke, scale]
  );

  const handlePointerUp = useCallback(() => {
    if (mode === 'draw' && draftStroke) {
      if (draftStroke.length >= 4) {
        store.addInk({
          page: pageNum,
          points: draftStroke.map(value => Math.round(value * 100) / 100),
          color: INK_COLOR,
          strokeWidth: INK_WIDTH,
          author,
        });
      }
      setDraftStroke(null);
      return;
    }

    if (mode === 'highlight' && draftRect) {
      if (draftRect.w > 2 && draftRect.h > 2) {
        // Snap the highlight to the text lines under the drag when possible.
        const lineRects: PdfRect[] = [];
        for (const line of textLayer?.lines ?? []) {
          const [x, y, w, h] = line.rect;
          const rect = { x, y, w, h };
          if (!rectsIntersect(rect, draftRect)) continue;
          const left = Math.max(rect.x, draftRect.x);
          const right = Math.min(rect.x + rect.w, draftRect.x + draftRect.w);
          if (right > left) {
            lineRects.push({ x: left, y: rect.y, w: right - left, h: rect.h });
          }
        }
        store.addHighlight({
          page: pageNum,
          rects: lineRects.length > 0 ? lineRects : [draftRect],
          color: HIGHLIGHT_COLOR,
          author,
        });
      }
      dragStart.current = null;
      setDraftRect(null);
    }
  }, [mode, draftStroke, draftRect, store, pageNum, author, textLayer]);

  useEffect(() => {
    // Leaving an interactive mode drops any unfinished draft state.
    setDraftStroke(null);
    setDraftRect(null);
    dragStart.current = null;
  }, [mode]);

  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    return observeResize(overlay, ({ contentRect }) =>
      setCssWidth(contentRect.width)
    );
  }, []);

  const inkEdits = edits.filter(
    (edit): edit is PdfInkEdit => edit.type === 'ink'
  );

  return (
    <div
      ref={overlayRef}
      className={styles.overlay}
      data-mode={mode}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      {scale > 0 && (
        <>
          {edits.map(edit => {
        switch (edit.type) {
          case 'whiteout':
          case 'text-replace':
            return (
              <WhiteoutRects
                key={`${edit.id}-rects`}
                edit={edit}
                scale={scale}
                onErase={() => eraseEdit(edit.id)}
              />
            );
          case 'highlight':
            return (
              <HighlightRects
                key={edit.id}
                edit={edit}
                scale={scale}
                onErase={() => eraseEdit(edit.id)}
              />
            );
          default:
            return null;
        }
      })}
      {(inkEdits.length > 0 || draftStroke) && (
        <svg
          className={styles.inkSvg}
          width="100%"
          height="100%"
          viewBox={`0 0 ${actualSize.width} ${actualSize.height}`}
          preserveAspectRatio="none"
        >
          {inkEdits.map(edit => (
            <InkStroke
              key={edit.id}
              edit={edit}
              onErase={() => eraseEdit(edit.id)}
            />
          ))}
          {draftStroke && draftStroke.length >= 4 && (
            <polyline
              className={styles.inkStroke}
              points={pointsToSvg(draftStroke)}
              stroke={INK_COLOR}
              strokeWidth={INK_WIDTH}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
        </svg>
      )}
      {edits.map(edit => {
        if (edit.type !== 'text-replace' && edit.type !== 'text-add') {
          return null;
        }
        return (
          <EditableText
            key={edit.id}
            edit={edit}
            scale={scale}
            mode={mode}
            autoFocus={edit.id === activeEditId}
            onErase={() => eraseEdit(edit.id)}
          />
        );
      })}
          {draftRect && (
            <div
              className={styles.highlightRect}
              style={{
                left: draftRect.x * scale,
                top: draftRect.y * scale,
                width: draftRect.w * scale,
                height: draftRect.h * scale,
                background: HIGHLIGHT_COLOR,
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

function pointsToSvg(points: number[]): string {
  const pairs: string[] = [];
  for (let i = 0; i + 1 < points.length; i += 2) {
    pairs.push(`${points[i]},${points[i + 1]}`);
  }
  return pairs.join(' ');
}

function WhiteoutRects({
  edit,
  scale,
  onErase,
}: {
  edit: PdfWhiteoutEdit | PdfTextReplaceEdit;
  scale: number;
  onErase: () => void;
}) {
  return (
    <>
      {edit.rects.map((rect, index) => (
        <div
          key={index}
          className={styles.whiteoutRect}
          style={{
            left: (rect.x - 0.5) * scale,
            top: (rect.y - 0.5) * scale,
            width: (rect.w + 1) * scale,
            height: (rect.h + 1) * scale,
          }}
          onClick={onErase}
        />
      ))}
    </>
  );
}

function HighlightRects({
  edit,
  scale,
  onErase,
}: {
  edit: PdfHighlightEdit;
  scale: number;
  onErase: () => void;
}) {
  return (
    <>
      {edit.rects.map((rect, index) => (
        <div
          key={index}
          className={styles.highlightRect}
          style={{
            left: rect.x * scale,
            top: rect.y * scale,
            width: rect.w * scale,
            height: rect.h * scale,
            background: edit.color,
          }}
          onClick={onErase}
        />
      ))}
    </>
  );
}

function InkStroke({
  edit,
  onErase,
}: {
  edit: PdfInkEdit;
  onErase: () => void;
}) {
  return (
    <polyline
      className={styles.inkStroke}
      points={pointsToSvg(edit.points)}
      stroke={edit.color}
      strokeWidth={edit.strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      onClick={onErase}
    />
  );
}

function EditableText({
  edit,
  scale,
  mode,
  autoFocus,
  onErase,
}: {
  edit: PdfTextReplaceEdit | PdfTextAddEdit;
  scale: number;
  mode: PdfEditMode;
  autoFocus: boolean;
  onErase: () => void;
}) {
  const [value, setValue] = useYText(edit.text);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [value, scale]);

  useEffect(() => {
    if (autoFocus) {
      textareaRef.current?.focus();
    }
  }, [autoFocus]);

  const style: CSSProperties = {
    left: edit.box.x * scale,
    top: edit.box.y * scale,
    width: Math.max(edit.box.w * scale, 48),
    minHeight: edit.box.h * scale,
    fontSize: edit.style.fontSize * scale,
    fontWeight: edit.style.fontWeight,
    fontFamily: cssFontFamily(edit.style.fontFamily),
    color: edit.style.color,
  };

  return (
    <textarea
      ref={textareaRef}
      className={styles.editText}
      style={style}
      value={value}
      readOnly={mode === 'erase' || mode === 'view'}
      spellCheck={false}
      wrap="off"
      title={edit.author ? `Edited by ${edit.author}` : undefined}
      onChange={event => setValue(event.target.value)}
      onClick={event => {
        event.stopPropagation();
        if (mode === 'erase') onErase();
      }}
      onPointerDown={event => event.stopPropagation()}
    />
  );
}
